import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../../utils/api';

export default function MemberEvents() {
  const { eventId } = useParams();
  const [events, setEvents] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [attendanceCode, setAttendanceCode] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('memberToken');
    if (!token) {
      navigate('/member-portal/login');
      return;
    }

    if (eventId) {
      fetchEventDetails(eventId);
    } else {
      fetchEvents();
    }
  }, [eventId, navigate]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/member-portal/events');
      setEvents(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const fetchEventDetails = async (id) => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/member-portal/events');
      const eventData = response.data.data?.find((e) => e.id === parseInt(id));
      if (eventData) {
        setEvent(eventData);
      } else {
        setError('Event not found');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch event details');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAttendance = async () => {
    if (!eventId) return;

    try {
      setError('');
      setSuccess('');
      await api.post(`/member-portal/events/${eventId}/confirm`);
      setSuccess('Attendance confirmed successfully!');
      setOpenDialog(false);
      fetchEventDetails(eventId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to confirm attendance');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (eventId && event) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button onClick={() => navigate('/member-portal/events')} sx={{ mb: 2 }}>
          ← Back to Events
        </Button>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Typography variant="h4" gutterBottom>
              {event.name}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {event.description}
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Date:</strong> {formatDate(event.date)}
              </Typography>
              {event.location && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Location:</strong> {event.location}
                </Typography>
              )}
              {event.max_attendees && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Max Attendees:</strong> {event.max_attendees}
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 3 }}>
              {event.attendance_confirmed ? (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Attendance Confirmed"
                  color="success"
                  size="large"
                />
              ) : (
                <Button
                  variant="contained"
                  onClick={() => setOpenDialog(true)}
                  sx={{ mt: 2 }}
                >
                  Confirm Attendance
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Confirm Attendance</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Are you sure you want to confirm your attendance for this event?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={handleConfirmAttendance} variant="contained">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Events
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : events.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No upcoming events
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {events.map((eventItem) => (
            <Card key={eventItem.id}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {eventItem.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {eventItem.description}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Date:</strong> {formatDate(eventItem.date)}
                    </Typography>
                    {eventItem.location && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Location:</strong> {eventItem.location}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ ml: 2 }}>
                    {eventItem.attendance_confirmed ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Confirmed"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(`/member-portal/events/${eventItem.id}`)}
                      >
                        View Details
                      </Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Container>
  );
}
